import { Button, message, Popconfirm } from "antd";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import Tooltip from "@mui/material/Tooltip";
import CloseIcon from "@mui/icons-material/Close";
import { Link, useNavigate } from "react-router-dom";
import moment from "moment";
import { backend_url } from "shared/config.js";
import { commentIcon, likeIconOutline, likeFill } from "../SvgIcons.jsx";
import { useEffect, useContext, useState } from "react";
import { AuthContext } from "../Auth/AuthProvider.js";

const PostBottom = ({
  post,
  postCreator,
  isPostCreator,
  answerers,
  commentIconBool = false,
  onDelete,
  reply_page = false,
  changeEditStatus,
  isEdit,
  onCommentIconClick,
}) => {
  const { user, loading, accessToken } = useContext(AuthContext);
  const navigate = useNavigate();

  const _id = post?.id;
  const [allLikers, setAllLikers] = useState(0);
  const [liked, setLiked] = useState(false);
  useEffect(() => {
    setLiked(user && post?.likers?.includes(user.uid));
    setAllLikers(post?.likers?.length || 0);
  }, [user, post]);

  const handleLike = async () => {
    try {
      if (!liked) {
        fetch(backend_url + "/update/post/add_like?post_id=" + _id, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer: " + accessToken,
          },
        });
        setAllLikers(allLikers + 1);
      } else {
        fetch(backend_url + "/update/post/remove_like?post_id=" + _id, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer: " + accessToken,
          },
        });
        setAllLikers(allLikers - 1);
      }
      setLiked(!liked);
    } catch (e) {
      console.error("Did not retrieve creator info", creatorId, e);
    }
  };

  const deletePost = async () => {
    try {
      await fetch(backend_url + "/delete/post?post_id=" + _id, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer: " + accessToken,
        },
      });
      console.log("Deleted Post id: ", _id);
      onDelete(_id);
    } catch (e) {
      console.error("Did not delete post", creatorId, e);
    }
  };
  const confirmDelete = (e) => {
    message.success("Deleted Post");
    deletePost();
  };

  const commentIconClick = () => {
    if (post?.type == 1) {
      navigate("/post/" + _id);
    } else if (reply_page && onCommentIconClick) {
      // For reply pages, trigger the inline comment form
      onCommentIconClick();
    }
    // For reply pages, inline comments will handle the commenting
  };
  return (
    <div className="bg-white rounded-lg border border-gray-100 shadow-sm mt-3 overflow-hidden">
      {/* Main Interaction Bar */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-50">
        {/* Left Side - Engagement Actions */}
        <div className="flex items-center space-x-6">
          <Tooltip title={liked ? "Unlike" : "Like Post"}>
            <button
              onClick={handleLike}
              className={`flex items-center space-x-2 px-3 py-1.5 rounded-full transition-all duration-200 ${
                liked
                  ? "bg-red-50 text-red-600 hover:bg-red-100"
                  : "text-gray-600 hover:bg-gray-50 hover:text-red-600"
              }`}
            >
              {liked ? likeFill : likeIconOutline}
              <span className="text-sm font-medium">{allLikers}</span>
            </button>
          </Tooltip>

          {commentIconBool && (
            <Tooltip title="Go to Discussion">
              <button
                onClick={commentIconClick}
                className="flex items-center space-x-2 px-3 py-1.5 rounded-full text-gray-600 hover:bg-blue-50 hover:text-blue-600 transition-all duration-200"
              >
                {commentIcon}
                <span className="text-sm font-medium">
                  {post?.replies?.length || 0}
                  {" Replies"}
                </span>
              </button>
            </Tooltip>
          )}

          {/* Answerers Preview */}
          {!reply_page && answerers?.length > 0 && (
            <Tooltip
              title={
                <div className="space-y-1">
                  {answerers.slice(0, 3).map((answerer) => (
                    <div key={answerer.link} className="text-xs">
                      {answerer.firstname} {answerer.lastname}
                    </div>
                  ))}
                  {answerers.length > 3 && (
                    <div className="text-xs opacity-75">
                      +{answerers.length - 3} more
                    </div>
                  )}
                </div>
              }
            >
              <div
                className="flex -space-x-2 cursor-pointer"
                onClick={() => navigate("/post/" + _id)}
              >
                {answerers.slice(0, 3).map((answerer, index) => (
                  <div
                    key={answerer.link}
                    className="w-7 h-7 bg-gradient-to-br from-purple-400 to-pink-400 rounded-full border-2 border-white flex items-center justify-center text-white text-xs font-medium"
                    style={{ zIndex: 10 - index }}
                  >
                    {answerer.firstname?.charAt(0)}
                    {answerer.lastname?.charAt(0)}
                  </div>
                ))}
                {answerers.length > 3 && (
                  <div className="w-7 h-7 bg-gray-300 rounded-full border-2 border-white flex items-center justify-center text-gray-600 text-xs font-medium">
                    +{answerers.length - 3}
                  </div>
                )}
              </div>
            </Tooltip>
          )}
        </div>

        {/* Right Side - Creator Actions */}
        <div className="flex items-center space-x-2">
          {isPostCreator && (
            <>
              <Tooltip title={isEdit ? "Cancel Edit" : "Edit Post"}>
                <button
                  data-testid={`edit-post-button-${post.id}`}
                  onClick={() => changeEditStatus()}
                  className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-full transition-all duration-200"
                >
                  {isEdit ? (
                    <CloseIcon fontSize="small" />
                  ) : (
                    <EditIcon fontSize="small" />
                  )}
                </button>
              </Tooltip>

              <Popconfirm
                title="Delete Post"
                description="Are you sure you want to delete this post?"
                onConfirm={confirmDelete}
                okText="Delete"
                cancelText="Cancel"
                okButtonProps={{ danger: true }}
              >
                <Tooltip title="Delete Post">
                  <button className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-full transition-all duration-200">
                    <DeleteOutlineIcon fontSize="small" />
                  </button>
                </Tooltip>
              </Popconfirm>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PostBottom;
